import { CheckCircleFilled, WarningFilled } from "@ant-design/icons"
import { Divider, Empty, Flex, Skeleton, Typography } from "antd"
import React, { useEffect, useRef, useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import type { RootState } from "../../../store.ts"
import { NOTIFICATION_PATHS } from "../../Notifications/api/services.ts"
import { parseNotificationMessage } from "../../Notifications/helpers/parseNotificationMessage.ts"
import type {
  Notification,
  NotificationCursor,
  NotificationLevel
} from "../../Notifications/types/notification.types.ts"
import { HOME_LIST_MAX_HEIGHT } from "./homeListHeight.ts"
import { homeCardStyle, homeColor, homeText } from "../home-theme.ts"
import { HomeSectionHeader } from "./HomeSectionHeader.tsx"

const { Text, Paragraph } = Typography

const iconMap: Record<NotificationLevel, React.ReactNode> = {
  success: <CheckCircleFilled style={{ fontSize: 17, color: homeColor.iconFg }} />,
  warning: <WarningFilled style={{ fontSize: 17, color: homeColor.warningFg }} />,
  error: <WarningFilled style={{ fontSize: 17, color: homeColor.negative }} />,
  info: <CheckCircleFilled style={{ fontSize: 17, color: homeColor.teal }} />,
  4: <WarningFilled style={{ fontSize: 17, color: homeColor.negative }} />
}

export function DashboardNotifications() {
  const { t } = useTranslation()
  const accessToken = useSelector((state: RootState) => state.auth.accessToken)
  const containerRef = useRef<HTMLDivElement>(null)
  const wsRef = useRef<WebSocket | null>(null)

  const [notifications, setNotifications] = useState<Notification[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [nextId, setNextId] = useState<NotificationCursor>(null)
  const [unreadCount, setUnreadCount] = useState(0)

  const onLoadMore = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: "fetch_more",
          after_id: nextId
        })
      )
    }
  }

  useEffect(() => {
    if (!hasMore) return

    const container = containerRef.current
    if (!container) return

    const hasNoScroll = container.scrollHeight <= container.clientHeight
    if (hasNoScroll && notifications.length > 0) {
      onLoadMore()
    }
  }, [notifications.length, hasMore])

  useEffect(() => {
    if (!accessToken) return

    wsRef.current = new WebSocket(
      NOTIFICATION_PATHS.getNotifications(accessToken)
    )

    wsRef.current.onmessage = (event) => {
      const data = parseNotificationMessage(event.data)
      if (!data) return

      if (data.type === "initial_state" && data.notifications) {
        setNotifications(data.notifications)
        setHasMore(data.has_more || false)
        setNextId(data.next_cursor)
        setUnreadCount(data.unread_count ?? 0)
      }

      if (data.type === "notifications_batch") {
        if (data.notifications) {
          setNotifications((prev) => [
            ...prev,
            ...(data.notifications as Notification[])
          ])
        }
        setNextId(data.next_cursor)
        setHasMore(data.has_more || false)
      }
    }

    return () => wsRef.current?.close()
  }, [accessToken])

  // The reference's own treatment: a pale blue surface carries an unread row,
  // a read one sits on the panel. Level 4 keeps its red tint — it is the one
  // level that means something went wrong.
  return (
    <section
      className="h-full flex flex-col"
      style={{ ...homeCardStyle, overflow: "hidden" }}
    >
      <HomeSectionHeader title={t("notifications.bell.title", { n: unreadCount })} />

      <div
        className="flex-1 overflow-auto tailwind-scroll p-2.5"
        id="notificationsScrollableDiv"
        ref={containerRef}
        style={{ maxHeight: HOME_LIST_MAX_HEIGHT }}
      >
        {notifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">{t("notifications.bell.empty")}</Text>}
          />
        ) : (
          <InfiniteScroll
            dataLength={notifications.length}
            next={onLoadMore}
            hasMore={hasMore}
            className="flex flex-col"
            loader={<Skeleton active paragraph={{ rows: 1 }} />}
            endMessage={<Divider />}
            scrollableTarget="notificationsScrollableDiv"
          >
            {notifications.map((notification, index) => (
              <Flex
                align="start"
                gap={10}
                key={notification.id}
                className="p-2.5"
                style={{
                  borderTop:
                    index === 0 ? undefined : `1px solid ${homeColor.border}`
                }}
              >
                <Flex
                  align="center"
                  justify="center"
                  className="size-6 shrink-0 mt-0.5"
                >
                  {iconMap[notification.level]}
                </Flex>
                <div className="flex-1 min-w-0">
                  {notification.description ? (
                    <Paragraph
                      className={"mb-0"}
                      ellipsis={{ rows: 2, expandable: true, symbol: "more" }}
                      strong={notification.unread}
                      style={{
                        ...homeText.body,
                        display: "block",
                        color: homeColor.ink
                      }}
                    >
                      {notification.description}
                    </Paragraph>
                  ) : (
                    // A row the backend sent with no text still has a time and
                    // an unread state worth showing; nothing is invented for it.
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      {t("notifications.bell.noDescription", "No details")}
                    </Text>
                  )}
                  <Text style={{ ...homeText.label, color: homeColor.inkMuted }}>
                    {t("notifications.bell.timeAgo", { time: notification.timesince })}
                  </Text>
                </div>
                {notification.unread && (
                  <span
                    className="size-2 rounded-full shrink-0 mt-2"
                    style={{ backgroundColor: homeColor.teal }}
                  />
                )}
              </Flex>
            ))}
          </InfiniteScroll>
        )}
      </div>
    </section>
  )
}
